import React from 'react';
import { useNavigate } from 'react-router-dom';

// Reusable User Avatar component with click-to-profile functionality
export const UserAvatar = ({ 
  userId, 
  name, 
  avatarUrl, 
  user,  // Alternative: pass user object directly
  size = 32, 
  showTooltip = true,
  clickable = true,
  isOnLeave = false,  // NEW: Show on leave indicator
  style = {}
}) => {
  const navigate = useNavigate();
  
  // Support both individual props and user object
  const resolvedUserId = userId || user?.id;
  const resolvedName = name || user?.full_name || user?.name;
  const resolvedAvatarUrl = avatarUrl || user?.avatar || user?.avatar_url;
  const resolvedOnLeave = isOnLeave || user?.is_on_leave;

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getColorFromName = (name) => {
    if (!name) return '#6B7280';
    const colors = ['#7077FE', '#10B981', '#F59E0B', '#EC4899', '#6366F1', '#14B8A6', '#8B5CF6', '#F97316'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  const handleClick = (e) => {
    if (clickable && resolvedUserId) {
      e.stopPropagation();
      navigate(`/dashboard/profile/${resolvedUserId}`);
    }
  };

  const displayName = cleanName(resolvedName);
  const bgColor = getColorFromName(displayName);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={handleClick}
        title={showTooltip ? (resolvedOnLeave ? `${displayName} (On Leave)` : displayName) : undefined}
        data-testid={resolvedUserId ? `user-avatar-${resolvedUserId}` : 'user-avatar'}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          background: resolvedAvatarUrl 
            ? `url(${resolvedAvatarUrl}) center/cover` 
            : `linear-gradient(135deg, ${bgColor}, ${bgColor}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${Math.max(size * 0.4, 10)}px`,
          fontWeight: '600',
          color: 'white',
          cursor: clickable && resolvedUserId ? 'pointer' : 'default',
          flexShrink: 0,
          border: resolvedOnLeave ? '2px solid #3B82F6' : '2px solid white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          opacity: resolvedOnLeave ? 0.7 : 1,
          ...style
        }}
        onMouseEnter={(e) => {
          if (clickable && resolvedUserId) {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }}
      >
        {!resolvedAvatarUrl && getInitials(displayName)}
      </div>
      {/* On Leave Badge */}
      {resolvedOnLeave && size >= 28 && (
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            width: `${Math.max(size * 0.4, 14)}px`,
            height: `${Math.max(size * 0.4, 14)}px`,
            borderRadius: '50%',
            background: '#3B82F6',
            border: '2px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '8px'
          }}
          title="On Leave"
        >
          🏖️
        </div>
      )}
    </div>
  );
};

// Clickable name that navigates to user profile
export const ClickableName = ({ 
  userId, 
  name, 
  style = {},
  showAvatar = false,
  avatarSize = 24,
  avatarUrl
}) => {
  const navigate = useNavigate();

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  const handleClick = (e) => {
    if (userId) {
      e.stopPropagation();
      navigate(`/dashboard/profile/${userId}`);
    }
  };

  const displayName = cleanName(name);

  return (
    <span
      onClick={handleClick}
      style={{
        cursor: userId ? 'pointer' : 'default',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: userId ? '#7077FE' : 'inherit',
        fontWeight: '500',
        transition: 'color 0.2s',
        ...style
      }}
      onMouseEnter={(e) => {
        if (userId) {
          e.currentTarget.style.textDecoration = 'underline';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {showAvatar && (
        <UserAvatar 
          userId={userId} 
          name={name} 
          avatarUrl={avatarUrl}
          size={avatarSize} 
          clickable={false}
          showTooltip={false}
        />
      )}
      {displayName}
    </span>
  );
};

// Avatar stack for multiple assignees
export const AvatarStack = ({ 
  users = [], 
  maxVisible = 3, 
  size = 28,
  showNames = false
}) => {
  const navigate = useNavigate();
  const visibleUsers = users.slice(0, maxVisible);
  const remaining = users.length - maxVisible;

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: showNames ? '8px' : '0' }}>
      <div style={{ display: 'flex', marginLeft: showNames ? '0' : '0' }}>
        {visibleUsers.map((user, index) => (
          <div 
            key={user.id || index} 
            style={{ marginLeft: index > 0 ? '-8px' : '0', zIndex: maxVisible - index }}
          >
            <UserAvatar
              userId={user.id}
              name={user.full_name || user.name}
              avatarUrl={user.avatar_url}
              size={size}
              showTooltip={true}
            />
          </div>
        ))}
        {remaining > 0 && (
          <div
            style={{
              marginLeft: '-8px',
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: '50%',
              background: 'var(--bg-tertiary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: `${Math.max(size * 0.35, 10)}px`,
              fontWeight: '600',
              color: 'var(--text-secondary)',
              border: '2px solid white',
              zIndex: 0
            }}
            title={`+${remaining} more`}
          >
            +{remaining}
          </div>
        )}
      </div>
      {showNames && visibleUsers.length > 0 && (
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {visibleUsers.map(u => cleanName(u.full_name || u.name)).join(', ')}
          {remaining > 0 && ` +${remaining} more`}
        </span>
      )}
    </div>
  );
};

export default UserAvatar;
